import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  DEFAULT_AVATAR,
  EMAIL_REGEXP,
  NO_AVATAR_SUFFIX
} from '@adsk/bim360-matrix-react-helpers/es/consts';
import './_UserAvatar.scss';

import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';
import { useTheme } from '@adsk/bim360-matrix-react-helpers/es/theme';

/**
 * UserAvatar component that displays the avatar or the initials if there's no image it displays the initials of the user.
 **/
const UserAvatar = props => {
  const { user, className, theme, ...rest } = props;
  const { theme: themeFromThemeProvider } = useTheme();

  // TODO: remove this logic when creating the User model so it'll be calculated there.
  const hasProfileImage = user.profileImg50 && !user.profileImg50.endsWith(NO_AVATAR_SUFFIX);
  const profileImage = hasProfileImage ? user.profileImg50 : DEFAULT_AVATAR;
  let initials = '';

  const classes = classNames(
    'UserAvatar',
    `UserAvatar--${theme}`,
    `UserAvatar--${themeFromThemeProvider}`,
    className
  );
  const isValidName = user.name && !EMAIL_REGEXP.test(user.name);
  if (isValidName) {
    const nameParts = user.name.split(' ');
    if (nameParts.length > 1) {
      initials = `${nameParts.shift()[0]}${nameParts.pop()[0]}`;
    } else {
      initials = nameParts.pop()[0];
    }
  }

  const displayedAvatar =
    hasProfileImage || !isValidName ? (
      <img className="UserAvatar__avatar" src={profileImage} />
    ) : (
      <div className="UserAvatar__initials">
        <div className="UserAvatar__initials-text">{initials}</div>
      </div>
    );

  return (
    <div {...rest} className={classes}>
      {displayedAvatar}
    </div>
  );
};

UserAvatar.Themes = {
  DARK: 'dark',
  LIGHT: 'light'
};

// In the future we're gonna have a user model so we'll use it instead.
// Right now for the sake of moving forward we're just "shaping" it.
UserAvatar.propTypes = {
  /**
   * The user to show the avatar for
   */
  user: PropTypes.shape({
    /**
     * The name of the user, used for displaying the initials when no profile image exists
     */
    name: PropTypes.string,
    /**
     * The profile image of the user to display as the avatar
     */
    profileImg50: PropTypes.string
  }).isRequired,
  /**
   * Class to add to the top level block
   */
  className: PropTypes.string,
  /**
   * The styling theme for the UserAvatar
   */
  theme: PropTypes.oneOf(objectValues(UserAvatar.Themes))
};

UserAvatar.defaultProps = {
  theme: UserAvatar.Themes.LIGHT
};

export default UserAvatar;
